import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ClientTestimonial from '../components/ClientTestimonial';
import GallerySection from '../components/GallerySection';
import SEO from '../components/SEO';
import { ArrowRight, Building2, Landmark, Zap, Wifi, Globe, Heart, CheckCircle, FileText, Shield, Users, Lock } from 'lucide-react';
import { trustedByLogos } from '../data/mock';

const sectors = [
  { name: "Financial Services and Banking", icon: Building2 },
  { name: "Development Finance Institutions", icon: Landmark },
  { name: "Energy and Natural Resources", icon: Zap },
  { name: "Telecommunications and Technology", icon: Wifi },
  { name: "Public Sector and Government", icon: Globe },
  { name: "Healthcare and Social Impact", icon: Heart }
];

const caseStudies = [
  {
    sector: "Financial Services",
    type: "Commercial Banking",
    title: "Strengthening leadership pipelines and execution",
    context: "A large commercial bank faced leadership pipeline gaps, uneven management capability, and slowing execution as the organisation scaled.",
    approach: [
      "Organisation-wide leadership diagnostics",
      "Leadership development programmes for first-time managers and senior leaders",
      "Coaching support for critical leadership roles"
    ],
    outcomes: [
      "Improved internal promotion rates",
      "Clearer succession visibility",
      "Measurable behavioural change across leadership cohorts",
      "Stronger execution discipline at senior levels"
    ]
  },
  {
    sector: "Development Finance Institution",
    type: "",
    title: "Culture reset and leadership alignment at scale",
    context: "A national development finance institution required a culture reset following organisational change, with a need to strengthen leadership tone, trust, and ethical behaviour.",
    approach: [
      "Culture and leadership diagnostics across large employee populations",
      "Leadership development and ethics-focused capability building",
      "Insight-led advisory support to senior leadership"
    ],
    outcomes: [
      "Restored trust and clarity around leadership expectations",
      "Stronger ethical tone and behavioural consistency",
      "Improved alignment between leadership behaviour and institutional mandate"
    ]
  },
  {
    sector: "Pan-African Development Institution",
    type: "",
    title: "Cross-regional cultural alignment",
    context: "A pan-African institution operating across multiple regions faced cultural misalignment, communication breakdowns, and conflict between teams.",
    approach: [
      "Team and culture diagnostics",
      "Targeted leadership and team interventions",
      "Behavioural insight integrated into leadership development"
    ],
    outcomes: [
      "Improved cross-cultural understanding",
      "Reduced conflict and stronger collaboration",
      "More consistent leadership behaviour across regions"
    ]
  },
  {
    sector: "Energy & Infrastructure",
    type: "",
    title: "Leadership capability for regulated, high-risk environments",
    context: "An energy sector organisation operating in a regulated environment needed to strengthen leadership capability, compliance awareness, and execution reliability.",
    approach: [
      "Leadership diagnostics and capability assessment",
      "Bespoke leadership development programmes",
      "Coaching support for senior leaders"
    ],
    outcomes: [
      "Improved leadership readiness",
      "Stronger compliance awareness",
      "Enhanced operational discipline and stakeholder confidence"
    ]
  },
  {
    sector: "Healthcare",
    type: "",
    title: "Building leadership capability in complex service environments",
    context: "Healthcare organisations facing regulatory pressure and operational complexity required stronger leadership capability to improve service delivery.",
    approach: [
      "Leadership development programmes tailored to healthcare contexts",
      "Coaching support for managers and senior leaders",
      "Insight-led programme design"
    ],
    outcomes: [
      "Improved operational efficiency",
      "Stronger leadership confidence",
      "Better alignment between leadership behaviour and care delivery priorities"
    ]
  }
];

const clientValues = [
  "Insight-led, evidence-based approaches",
  "Deep understanding of African and global organisational contexts",
  "Ability to operate at scale without losing quality",
  "Strong governance and professional standards",
  "Measurable behavioural and performance outcomes"
];

const workSpans = [
  "Boards and executive teams",
  "Senior and middle management",
  "Frontline leaders and critical talent pools"
];

const CaseStudiesPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Leadership Case Studies Nigeria | Client Success Stories Lagos"
        description="Leadership development and executive coaching case studies from Nigeria's top organisations. See how Nigerian banks, corporates, and multinationals transformed leadership effectiveness with RESILIGENT. Real results, measurable impact."
        keywords="leadership case studies Nigeria, executive coaching success stories Lagos, organisational development case studies Africa, leadership transformation Nigeria, coaching outcomes Lagos, leadership ROI Nigeria"
        canonical="/case-studies"
      />
      <Header />
      
      <main>
        {/* Hero Section with Background Image */}
        <section 
          className="relative min-h-[70vh] lg:min-h-[80vh] flex items-center bg-cover bg-no-repeat"
          style={{
            backgroundImage: `linear-gradient(to right, rgba(10, 10, 10, 0.95) 0%, rgba(10, 10, 10, 0.8) 40%, rgba(10, 10, 10, 0.4) 100%), url('/opt/photo/image-3.webp')`,
            backgroundPosition: 'center 10%'
          }}
        >
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8 pt-24">
            <div className="max-w-3xl">
              <h1 className="text-white font-semibold text-3xl sm:text-4xl lg:text-5xl leading-tight mb-6">
                Case Studies & Clients
              </h1>
              
              <p className="text-[#2693d0] text-lg lg:text-xl font-medium leading-relaxed mb-6">
                Trusted by organisations navigating leadership, culture, and change
              </p>
              
              <p className="text-slate-300 text-base lg:text-lg leading-relaxed mb-6">
                RESILIGENT works with organisations across sectors that are navigating growth, complexity, regulation, and transformation.
              </p>
              
              <p className="text-slate-400 text-base lg:text-lg leading-relaxed">
                Our clients engage us when leadership behaviour, culture, and capability directly affect performance, trust, and execution.
              </p>
            </div>
          </div>
        </section>

        {/* Our Clients - Sectors */}
        <section className="bg-white py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl mb-12">
              <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-8 leading-tight">
                Our Clients
              </h2>
              <p className="text-slate-600 text-base lg:text-lg leading-relaxed">
                We have worked with organisations across:
              </p>
            </div>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
              {sectors.map((sector, index) => {
                const Icon = sector.icon;
                return (
                  <div key={index} className="bg-[#f0f5f8] rounded-xl p-6 border border-slate-100 flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-[#2693d0]/10 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-[#2693d0]" />
                    </div>
                    <span className="text-slate-700 text-sm font-medium">{sector.name}</span>
                  </div>
                );
              })}
            </div>
            
            <p className="text-slate-600 text-base lg:text-lg leading-relaxed max-w-3xl">
              Our work spans organisations operating at national, regional, and pan-African scale.
            </p>
          </div>
        </section>

        {/* Selected Case Studies Intro */}
        <section className="relative bg-[#f0f5f8] py-16 lg:py-20">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-6 leading-tight">
                Selected Case Studies
              </h2>
              <p className="text-slate-600 text-base lg:text-lg leading-relaxed mb-4">
                The examples below illustrate how organisations engage RESILIGENT in practice.
              </p>
              <p className="text-slate-500 text-base leading-relaxed">
                Details are shared at a level appropriate to confidentiality and governance.
              </p>
            </div>
          </div>
        </section>

        {/* Case Studies */}
        <section className="bg-white py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="space-y-16">
              {caseStudies.map((study, index) => (
                <div key={index} className="border-b border-slate-100 pb-16 last:border-b-0 last:pb-0">
                  <div className="grid lg:grid-cols-3 gap-8 lg:gap-12">
                    {/* Header */}
                    <div>
                      <div className="mb-4">
                        <p className="text-[#2693d0] text-sm font-semibold tracking-wide uppercase">
                          {study.sector}
                          {study.type && <span className="text-slate-400 font-normal"> | {study.type}</span>}
                        </p>
                      </div>
                      <h3 className="text-slate-800 font-semibold text-xl lg:text-2xl mb-4 leading-tight">
                        {study.title}
                      </h3>
                      <div className="bg-[#f0f5f8] rounded-lg p-4 border border-slate-100">
                        <p className="text-[#2693d0] text-xs font-medium uppercase tracking-wide mb-2">Context</p>
                        <p className="text-slate-600 text-sm leading-relaxed">{study.context}</p>
                      </div>
                    </div>
                    
                    {/* Approach */}
                    <div>
                      <p className="text-[#2693d0] text-sm font-medium uppercase tracking-wide mb-4">Our Approach</p>
                      <ul className="space-y-3">
                        {study.approach.map((item, idx) => (
                          <li key={idx} className="flex items-start gap-3">
                            <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2 flex-shrink-0" />
                            <span className="text-slate-600 text-sm leading-relaxed">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    {/* Outcome */}
                    <div>
                      <p className="text-[#2693d0] text-sm font-medium uppercase tracking-wide mb-4">The Outcome</p>
                      <ul className="space-y-3">
                        {study.outcomes.map((item, idx) => (
                          <li key={idx} className="flex items-start gap-3">
                            <CheckCircle className="w-4 h-4 text-[#2693d0] flex-shrink-0 mt-0.5" />
                            <span className="text-slate-700 text-sm leading-relaxed">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Client Testimonial - Omar Shekarau */}
        <section className="bg-slate-100 py-16 lg:py-20">
          <div className="w-full max-w-4xl mx-auto px-6 lg:px-8">
            <ClientTestimonial
              quote="RESILIGENT delivered leadership development from frontline teams through to board level. The experience was practical, refreshing, and sufficiently valuable that I would recommend it as a regular organisational investment."
              name="Omar Shekarau"
              title="Executive Director"
              company="Bank of Industry"
              variant="light"
            />
          </div>
        </section>

        {/* Clients Video Case Study */}
        <section className="relative bg-[#f0f5f8] py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-4 leading-tight">
                Client Video Case Study
              </h2>
              <p className="text-slate-600 text-base lg:text-lg max-w-2xl mx-auto">
                Hear directly from our clients about their experience working with RESILIGENT.
              </p>
            </div>
            
            <div className="max-w-4xl mx-auto">
              <div className="relative w-full aspect-video rounded-xl overflow-hidden shadow-lg">
                <iframe
                  src="https://www.youtube.com/embed/osAqH1hVklo"
                  title="RESILIGENT Client Case Study"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full"
                />
              </div>
            </div>
          </div>
        </section>

        {/* What Our Clients Value */}
        <section className="bg-[#1e3a5f] py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
              <div>
                <h2 className="text-white font-semibold text-2xl sm:text-3xl lg:text-4xl mb-6 leading-tight">
                  What Our Clients Value
                </h2>
                <p className="text-slate-300 text-base lg:text-lg leading-relaxed">
                  Across sectors, clients consistently value RESILIGENT for:
                </p>
              </div>
              
              <div>
                <ul className="space-y-4">
                  {clientValues.map((item, index) => (
                    <li key={index} className="flex items-start gap-4">
                      <CheckCircle className="w-5 h-5 text-[#2693d0] flex-shrink-0 mt-0.5" />
                      <span className="text-slate-200 text-base lg:text-lg leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* From Boardroom to Frontline */}
        <section className="bg-white py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
              <div>
                <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-8 leading-tight">
                  From Boardroom to Frontline
                </h2>
                <p className="text-slate-600 text-base lg:text-lg leading-relaxed">
                  Our work spans:
                </p>
              </div>
              
              <div>
                <ul className="space-y-4 mb-8">
                  {workSpans.map((item, index) => (
                    <li key={index} className="flex items-center gap-4">
                      <div className="w-8 h-8 rounded-lg bg-[#2693d0]/10 flex items-center justify-center flex-shrink-0">
                        <Users className="w-4 h-4 text-[#2693d0]" />
                      </div>
                      <span className="text-slate-700 text-base lg:text-lg leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="bg-[#f0f5f8] rounded-xl p-6 border border-slate-100">
                  <p className="text-slate-800 font-medium text-base lg:text-lg leading-relaxed">
                    This allows organisations to build coherent leadership systems, rather than isolated interventions.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Confidentiality and Trust */}
        <section className="relative bg-[#f0f5f8] py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-xl bg-[#2693d0]/10 flex items-center justify-center">
                  <Lock className="w-6 h-6 text-[#2693d0]" />
                </div>
                <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl leading-tight">
                  Confidentiality and Trust
                </h2>
              </div>
              
              <p className="text-slate-600 text-base lg:text-lg leading-relaxed mb-6">
                Many of our engagements involve sensitive leadership, culture, or governance issues.
              </p>
              
              <p className="text-slate-600 text-base lg:text-lg leading-relaxed mb-6">
                We therefore share case material responsibly, balancing transparency with discretion.
              </p>
              
              <p className="text-slate-800 font-medium text-base lg:text-lg leading-relaxed">
                Further detail can be discussed in confidential conversations.
              </p>
            </div>
          </div>
        </section>

        {/* Gallery Section - Our Work In Action */}
        <GallerySection />

        {/* CTA Section */}
        <section className="bg-[#e8f2f7] py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-6 leading-tight">
                Next Steps
              </h2>
              
              {/* Primary CTA */}
              <div className="bg-white card-pulse-border rounded-xl p-8 mb-6 shadow-sm">
                <h3 className="text-slate-800 font-semibold text-xl mb-4">
                  Discuss a relevant case
                </h3>
                <p className="text-slate-600 text-base leading-relaxed mb-6">
                  A conversation to explore similar challenges, approaches, and outcomes in your organisational context.
                </p>
                <a
                  href="mailto:info@resiligent.com?subject=Case%20Study%20Discussion"
                  className="inline-flex items-center gap-2 bg-[#2693d0] text-white px-6 py-3.5 rounded font-medium text-base hover:bg-[#1e7ab8] transition-all duration-300"
                >
                  Start a Conversation
                  <ArrowRight size={18} />
                </a>
              </div>

              {/* Secondary CTA */}
              <a
                href="/engagement"
                className="inline-flex items-center gap-3 text-slate-600 hover:text-[#2693d0] transition-colors duration-300"
              >
                <FileText className="w-5 h-5" />
                <span className="text-base font-medium">Explore how organisations engage RESILIGENT</span>
                <ArrowRight size={16} />
              </a>
            </div>
          </div>
        </section>

        {/* Footer Note */}
        <section className="bg-[#e8f2f7] pb-12 border-t border-slate-200">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8 pt-8">
            <p className="text-slate-500 text-sm">
              Client examples are indicative and adapted to respect confidentiality and governance requirements.
            </p>
          </div>
        </section>

        {/* Client Logo Carousel Strip - Organisations we have worked with */}
        <section className="bg-white py-14 lg:py-20 border-y border-slate-100">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8 mb-8">
            <p className="text-slate-400 text-sm uppercase tracking-wider text-center">
              Some of the organisations we have worked with
            </p>
          </div>
          <div className="relative overflow-hidden">
            {/* Gradient masks for smooth edges */}
            <div className="absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-white to-transparent z-10" />
            <div className="absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-white to-transparent z-10" />
            
            {/* Scrolling logos */}
            <div className="flex animate-scroll">
              {[...trustedByLogos, ...trustedByLogos].map((logo, index) => (
                <div
                  key={index}
                  className="flex-shrink-0 h-16 lg:h-20 px-10 lg:px-14 flex items-center"
                >
                  <img
                    src={logo.url}
                    alt={logo.name}
                    className="h-full w-auto object-contain max-w-[180px]"
                  />
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer hidePreFooterCta={true} />
    </div>
  );
};

export default CaseStudiesPage;
